import { useState, useEffect } from 'react'
import { useParams, useSearchParams, useNavigate, useOutletContext } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  Settings, 
  Save, 
  Building, 
  Bell, 
  Shield, 
  Palette, 
  Key,
  Plus,
  Copy,
  Check,
  Trash2,
  Eye,
  EyeOff,
  AlertCircle,
  Calendar
} from 'lucide-react'
import { getFirebaseDb } from '@/lib/firebase'
import { useAuth } from '@/contexts/AuthContext'
import { collection, getDocs, doc, updateDoc, addDoc, deleteDoc, serverTimestamp, getDoc, query, where, orderBy } from 'firebase/firestore'
import { useTheme } from '@/contexts/ThemeContext'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { generateApiKey as generateJwtApiKey } from '@/lib/api-keys'
import { InviteMembers } from '@/components/InviteMembers'
import { OrganizationLogoUpload } from '@/components/OrganizationLogoUpload'
import { OrganizationContext } from './AssistPage'
import { getCallableFunction } from '@/lib/firebase'

interface Organization {
  id: string
  name: string
  slug: string
  website?: string
  description?: string
  logoUrl?: string | null
  enabledFeatures?: string[]
  require2FA?: boolean
}

interface ApiKey {
  id: string
  name: string
  key?: string
  keyId?: string // JWT ID for identification
  createdAt: Date
  lastUsed?: Date
  createdBy: string
}

interface AdminKey {
  id: string
  name: string
  key?: string // Only available on creation
  keyPrefix: string
  scopes: string[]
  createdAt?: string
  expiresAt?: string
  lastUsedAt?: string
}

export function SettingsPage() {
  const { orgId } = useParams<{ orgId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { theme, setTheme } = useTheme()
  const orgContext = useOutletContext<OrganizationContext>()
  
  // Check if user can modify settings (admin or super-admin only)
  const canModifySettings = orgContext?.isOrgAdmin || false
  
  // Get initial tab from URL or default based on role
  // Non-admins can only see notifications tab
  const getDefaultTab = () => {
    const urlTab = searchParams.get('tab')
    if (urlTab) {
      // If non-admin tries to access admin tab, redirect to notifications
      if (!canModifySettings && ['general', 'members', 'api-keys', 'admin-keys', 'security'].includes(urlTab)) {
        return 'notifications'
      }
      return urlTab
    }
    return canModifySettings ? 'general' : 'notifications'
  }
  const [activeTab, setActiveTab] = useState(getDefaultTab())
  
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [organizationId, setOrganizationId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  
  // Form state
  const [name, setName] = useState('')
  const [website, setWebsite] = useState('')
  const [description, setDescription] = useState('')
  
  // Notification preferences
  const [emailNotifications, setEmailNotifications] = useState(true)
  const [weeklyReports, setWeeklyReports] = useState(true)
  const [alertsEnabled, setAlertsEnabled] = useState(true)
  
  // Security settings
  const [require2FA, setRequire2FA] = useState(false)
  
  // API Keys state
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  const [isCreateKeyOpen, setIsCreateKeyOpen] = useState(false)
  const [newKeyName, setNewKeyName] = useState('')
  const [newKey, setNewKey] = useState<string | null>(null)
  const [creatingKey, setCreatingKey] = useState(false)
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set())

  // Admin Keys state (for programmatic API access)
  const [adminKeys, setAdminKeys] = useState<AdminKey[]>([])
  const [isCreateAdminKeyOpen, setIsCreateAdminKeyOpen] = useState(false)
  const [newAdminKeyName, setNewAdminKeyName] = useState('')
  const [newAdminKey, setNewAdminKey] = useState<string | null>(null)
  const [creatingAdminKey, setCreatingAdminKey] = useState(false)
  const [loadingAdminKeys, setLoadingAdminKeys] = useState(false)
  
  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark')
  }

  // Update URL when tab changes
  const handleTabChange = (value: string) => {
    setActiveTab(value)
    navigate(`/${orgId}/assist/settings?tab=${value}`, { replace: true })
  }

  useEffect(() => {
    const fetchData = async () => {
      // Get organization ID from outlet context
      const organizationId = orgContext?.organizationId
      if (!organizationId) {
        setLoading(false)
        return
      }
      
      try {
        const db = getFirebaseDb()
        
        // Read org by ID (from context)
        const orgDocRef = doc(db, 'organizations', organizationId)
        const orgDoc = await getDoc(orgDocRef)
        
        if (orgDoc.exists()) {
          const orgData = {
            id: orgDoc.id,
            ...orgDoc.data()
          } as Organization
          
          setOrganization(orgData)
          setOrganizationId(orgDoc.id)
          setName(orgData.name)
          setWebsite(orgData.website || '')
          setDescription(orgData.description || '')
          setRequire2FA(orgData.require2FA || false)
          
          // Fetch API keys
          const keysRef = collection(db, 'organizations', orgDoc.id, 'apiKeys')
          const keysSnapshot = await getDocs(keysRef)
          
          const keysData = keysSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date(),
            lastUsed: doc.data().lastUsed?.toDate()
          })) as ApiKey[]
          
          setApiKeys(keysData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()))
        }
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [orgContext?.organizationId])

  // Fetch admin keys when the admin-keys tab is active (direct Firestore access)
  const fetchAdminKeys = async () => {
    if (!organizationId) return

    setLoadingAdminKeys(true)
    try {
      const db = getFirebaseDb()
      const adminKeysRef = collection(db, 'organizations', organizationId, 'adminKeys')
      const q = query(
        adminKeysRef,
        where('revokedAt', '==', null),
        orderBy('createdAt', 'desc')
      )
      const snapshot = await getDocs(q)

      const keys: AdminKey[] = snapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          name: data.name,
          keyPrefix: data.keyPrefix,
          scopes: data.scopes || [],
          createdAt: data.createdAt?.toDate?.()?.toISOString(),
          expiresAt: data.expiresAt?.toDate?.()?.toISOString(),
          lastUsedAt: data.lastUsedAt?.toDate?.()?.toISOString(),
        }
      })

      setAdminKeys(keys)
    } catch (error) {
      console.error('Error fetching admin keys:', error)
    } finally {
      setLoadingAdminKeys(false)
    }
  }

  useEffect(() => {
    if (activeTab === 'admin-keys' && organizationId && canModifySettings) {
      fetchAdminKeys()
    }
  }, [activeTab, organizationId, canModifySettings])

  // Save require2FA setting when it changes
  useEffect(() => {
    const saveRequire2FA = async () => {
      if (!organization || loading) return
      
      try {
        const db = getFirebaseDb()
        const orgRef = doc(db, 'organizations', organization.id)
        await updateDoc(orgRef, { require2FA })
      } catch (error) {
        console.error('Error saving 2FA requirement:', error)
      }
    }
    
    saveRequire2FA()
  }, [require2FA, organization?.id, loading])

  const handleSaveOrganization = async () => {
    if (!organization) return
    
    setSaving(true)
    try {
      const db = getFirebaseDb()
      const orgRef = doc(db, 'organizations', organization.id)
      
      await updateDoc(orgRef, {
        name: name.trim(),
        website: website.trim() || null,
        description: description.trim() || null
      })
      
      setOrganization({
        ...organization,
        name: name.trim(),
        website: website.trim(),
        description: description.trim()
      })
    } catch (error) {
      console.error('Error saving organization:', error)
    } finally {
      setSaving(false)
    }
  }

  // API Key functions - uses JWT generation
  const handleCreateKey = async () => {
    if (!organizationId || !newKeyName.trim() || !user) return
    
    setCreatingKey(true)
    
    try {
      // Generate JWT token
      const result = await generateJwtApiKey({
        organizationId,
        name: newKeyName.trim(),
        expiresIn: '90d', // Default 90 days
      })
      
      if (!result.success || !result.token) {
        console.error('Failed to generate API key:', result.error)
        setCreatingKey(false)
        return
      }
      
      const db = getFirebaseDb()
      const keysRef = collection(db, 'organizations', organizationId, 'apiKeys')
      
      // Store key metadata (NOT the token itself)
      const docRef = await addDoc(keysRef, {
        name: newKeyName.trim(),
        keyId: result.keyId, // JWT ID for identification
        createdAt: serverTimestamp(),
        createdBy: user.uid
      })
      
      const newApiKey: ApiKey = {
        id: docRef.id,
        name: newKeyName.trim(),
        key: result.token, // Show token only once
        keyId: result.keyId,
        createdAt: new Date(),
        createdBy: user.uid
      }
      
      setApiKeys([newApiKey, ...apiKeys])
      setNewKey(result.token)
    } catch (error) {
      console.error('Error creating API key:', error)
    } finally {
      setCreatingKey(false)
    }
  }

  const handleDeleteKey = async (keyId: string) => {
    if (!organizationId) return
    
    try {
      const db = getFirebaseDb()
      await deleteDoc(doc(db, 'organizations', organizationId, 'apiKeys', keyId))
      setApiKeys(apiKeys.filter(k => k.id !== keyId))
    } catch (error) {
      console.error('Error deleting API key:', error)
    }
  }

  const copyToClipboard = async (text: string, keyId: string) => {
    await navigator.clipboard.writeText(text)
    setCopiedKey(keyId)
    setTimeout(() => setCopiedKey(null), 2000)
  }

  const toggleKeyVisibility = (keyId: string) => {
    const newVisible = new Set(visibleKeys)
    if (newVisible.has(keyId)) {
      newVisible.delete(keyId)
    } else {
      newVisible.add(keyId)
    }
    setVisibleKeys(newVisible)
  }

  const maskKey = (key: string | undefined) => {
    if (!key) return '••••••••••••••••••••••••••••••••'
    return key.substring(0, 8) + '••••••••••••••••••••••••'
  }

  const closeKeyDialog = () => {
    setIsCreateKeyOpen(false)
    setNewKeyName('')
    setNewKey(null)
  }

  // Admin Key functions
  const handleCreateAdminKey = async () => {
    if (!organizationId || !newAdminKeyName.trim()) return

    setCreatingAdminKey(true)
    try {
      const createAdminKey = getCallableFunction<
        { organizationId: string; name: string; scopes?: string[]; expiresInDays?: number },
        { id?: string; name?: string; key?: string; keyPrefix?: string; scopes?: string[]; expiresAt?: string; message?: string; error?: string; debug?: unknown }
      >('createAdminKey')

      const result = await createAdminKey({
        organizationId,
        name: newAdminKeyName.trim(),
        expiresInDays: 90, // Default 90 days
      })

      // Check for debug/error response
      if (result.data.debug) {
        console.log('[createAdminKey] DEBUG INFO:', JSON.stringify(result.data.debug, null, 2))
      }
      if (result.data.error) {
        console.error('[createAdminKey] Server error:', result.data.error)
        return
      }

      if (result.data.key) {
        setNewAdminKey(result.data.key)

        // Add to list (without the full key)
        const newKey: AdminKey = {
          id: result.data.id!,
          name: result.data.name!,
          keyPrefix: result.data.keyPrefix!,
          scopes: result.data.scopes!,
          expiresAt: result.data.expiresAt,
        }
        setAdminKeys([newKey, ...adminKeys])
      }
    } catch (error) {
      console.error('Error creating admin key:', error)
    } finally {
      setCreatingAdminKey(false)
    }
  }

  const handleRevokeAdminKey = async (keyId: string) => {
    if (!organizationId) return

    try {
      const db = getFirebaseDb()
      const keyRef = doc(db, 'organizations', organizationId, 'adminKeys', keyId)
      await updateDoc(keyRef, {
        revokedAt: serverTimestamp()
      })
      setAdminKeys(adminKeys.filter(k => k.id !== keyId))
    } catch (error) {
      console.error('Error revoking admin key:', error)
    }
  }

  const closeAdminKeyDialog = () => {
    setIsCreateAdminKeyOpen(false)
    setNewAdminKeyName('')
    setNewAdminKey(null)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <Settings className="h-8 w-8" />
        <div>
          <h1 className="text-3xl font-bold">Settings</h1>
          <p className="text-muted-foreground">
            Manage your organization and preferences
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList>
          {canModifySettings && <TabsTrigger value="general">General</TabsTrigger>}
          {canModifySettings && <TabsTrigger value="members">Members</TabsTrigger>}
          {canModifySettings && <TabsTrigger value="api-keys">API Keys</TabsTrigger>}
          {canModifySettings && <TabsTrigger value="admin-keys">Admin Keys</TabsTrigger>}
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          {canModifySettings && <TabsTrigger value="security">Security</TabsTrigger>}
        </TabsList>

        {/* General Tab - Admin Only */}
        {canModifySettings && (
        <TabsContent value="general" className="space-y-6">
          {/* Organization Settings */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Building className="h-5 w-5" />
                <CardTitle>Organization Settings</CardTitle>
              </div>
              <CardDescription>
                Update your organization's profile information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Organization Logo */}
              <div className="space-y-2">
                <Label>Organization Logo</Label>
                {organizationId && (
                  <OrganizationLogoUpload
                    organizationId={organizationId}
                    currentLogoUrl={organization?.logoUrl}
                    onLogoChange={(url) => {
                      if (organization) {
                        setOrganization({ ...organization, logoUrl: url })
                      }
                    }}
                  />
                )}
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="org-name">Organization Name</Label>
                  <Input
                    id="org-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your organization name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="https://example.com"
                    type="url"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of your organization"
                />
              </div>
              <Button onClick={handleSaveOrganization} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </CardContent>
          </Card>

          {/* Appearance */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Palette className="h-5 w-5" />
                <CardTitle>Appearance</CardTitle>
              </div>
              <CardDescription>
                Customize how the application looks
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Dark Mode</Label>
                  <p className="text-sm text-muted-foreground">
                    Switch between light and dark themes
                  </p>
                </div>
                <Switch
                  checked={theme === 'dark'}
                  onCheckedChange={toggleTheme}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Members Tab - Admin Only */}
        {canModifySettings && (
        <TabsContent value="members" className="space-y-6">
          {organizationId && organization && (
            <InviteMembers 
              organizationId={organizationId} 
              organizationName={organization.name} 
            />
          )}
        </TabsContent>
        )}

        {/* API Keys Tab - Admin Only */}
        {canModifySettings && (
        <TabsContent value="api-keys" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Key className="h-5 w-5" />
                  <div>
                    <CardTitle>API Keys</CardTitle>
                    <CardDescription>
                      Manage API keys for accessing the Signals API
                    </CardDescription>
                  </div>
                </div>
                <Dialog open={isCreateKeyOpen} onOpenChange={setIsCreateKeyOpen}>
                  <DialogTrigger asChild>
                    <Button onClick={() => { setNewKeyName(''); setNewKey(null); }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Key
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>
                        {newKey ? 'API Key Created' : 'Create API Key'}
                      </DialogTitle>
                      <DialogDescription>
                        {newKey 
                          ? 'Your new API key has been created. Copy it now - you won\'t be able to see it again.'
                          : 'Create a new API key for accessing the Signals API.'
                        }
                      </DialogDescription>
                    </DialogHeader>
                    
                    {newKey ? (
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <AlertCircle className="h-4 w-4 text-yellow-500" />
                          <span className="text-sm text-yellow-500">
                            Make sure to copy your API key now. You won't be able to see it again!
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Input
                            value={newKey}
                            readOnly
                            className="font-mono text-sm"
                          />
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => copyToClipboard(newKey, 'new')}
                          >
                            {copiedKey === 'new' ? (
                              <Check className="h-4 w-4 text-green-500" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                        <DialogFooter>
                          <Button onClick={closeKeyDialog}>Done</Button>
                        </DialogFooter>
                      </div>
                    ) : (
                      <>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label htmlFor="key-name">Key Name</Label>
                            <Input
                              id="key-name"
                              value={newKeyName}
                              onChange={(e) => setNewKeyName(e.target.value)}
                              placeholder="e.g., Production Key"
                            />
                            <p className="text-xs text-muted-foreground">
                              Give your key a descriptive name to identify its purpose
                            </p>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={closeKeyDialog}>
                            Cancel
                          </Button>
                          <Button 
                            onClick={handleCreateKey} 
                            disabled={creatingKey || !newKeyName.trim()}
                          >
                            {creatingKey ? 'Creating...' : 'Create Key'}
                          </Button>
                        </DialogFooter>
                      </>
                    )}
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {apiKeys.length === 0 ? (
                <div className="text-center py-12">
                  <Key className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No API Keys</h3>
                  <p className="text-muted-foreground mb-4">
                    Create an API key to start sending signals to the platform.
                  </p>
                  <Button onClick={() => setIsCreateKeyOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Your First Key
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-sm text-muted-foreground mb-4">
                    {apiKeys.length} {apiKeys.length === 1 ? 'key' : 'keys'} total
                  </div>
                  {apiKeys.map((apiKey) => (
                    <div
                      key={apiKey.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="space-y-1">
                        <div className="font-medium">{apiKey.name}</div>
                        <div className="flex items-center space-x-2">
                          <code className="text-sm font-mono text-muted-foreground">
                            {visibleKeys.has(apiKey.id) ? (apiKey.key || 'Key not available') : maskKey(apiKey.key)}
                          </code>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => toggleKeyVisibility(apiKey.id)}
                          >
                            {visibleKeys.has(apiKey.id) ? (
                              <EyeOff className="h-3 w-3" />
                            ) : (
                              <Eye className="h-3 w-3" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => apiKey.key && copyToClipboard(apiKey.key, apiKey.id)}
                            disabled={!apiKey.key}
                          >
                            {copiedKey === apiKey.id ? (
                              <Check className="h-3 w-3 text-green-500" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                        <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                          <span className="flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            Created {apiKey.createdAt.toLocaleDateString()}
                          </span>
                          {apiKey.lastUsed && (
                            <span>Last used {apiKey.lastUsed.toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete API Key</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{apiKey.name}"? This action cannot be undone and any applications using this key will stop working.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteKey(apiKey.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Admin Keys Tab - Admin Only */}
        {canModifySettings && (
        <TabsContent value="admin-keys" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Key className="h-5 w-5" />
                  <div>
                    <CardTitle>Admin Keys</CardTitle>
                    <CardDescription>
                      Manage admin keys for programmatic access to the Admin API (models, docs, etc.)
                    </CardDescription>
                  </div>
                </div>
                <Dialog open={isCreateAdminKeyOpen} onOpenChange={setIsCreateAdminKeyOpen}>
                  <DialogTrigger asChild>
                    <Button onClick={() => { setNewAdminKeyName(''); setNewAdminKey(null); }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Admin Key
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>
                        {newAdminKey ? 'Admin Key Created' : 'Create Admin Key'}
                      </DialogTitle>
                      <DialogDescription>
                        {newAdminKey
                          ? 'Your new admin key has been created. Copy it now - you won\'t be able to see it again.'
                          : 'Create a new admin key for programmatic API access to manage models, docs, and other resources.'
                        }
                      </DialogDescription>
                    </DialogHeader>

                    {newAdminKey ? (
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <AlertCircle className="h-4 w-4 text-yellow-500" />
                          <span className="text-sm text-yellow-500">
                            Make sure to copy your admin key now. You won't be able to see it again!
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Input
                            value={newAdminKey}
                            readOnly
                            className="font-mono text-sm"
                          />
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => copyToClipboard(newAdminKey, 'new-admin')}
                          >
                            {copiedKey === 'new-admin' ? (
                              <Check className="h-4 w-4 text-green-500" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                        <DialogFooter>
                          <Button onClick={closeAdminKeyDialog}>Done</Button>
                        </DialogFooter>
                      </div>
                    ) : (
                      <>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label htmlFor="admin-key-name">Key Name</Label>
                            <Input
                              id="admin-key-name"
                              value={newAdminKeyName}
                              onChange={(e) => setNewAdminKeyName(e.target.value)}
                              placeholder="e.g., CI/CD Pipeline Key"
                            />
                            <p className="text-xs text-muted-foreground">
                              Give your key a descriptive name to identify its purpose
                            </p>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={closeAdminKeyDialog}>
                            Cancel
                          </Button>
                          <Button
                            onClick={handleCreateAdminKey}
                            disabled={creatingAdminKey || !newAdminKeyName.trim()}
                          >
                            {creatingAdminKey ? 'Creating...' : 'Create Key'}
                          </Button>
                        </DialogFooter>
                      </>
                    )}
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {loadingAdminKeys ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : adminKeys.length === 0 ? (
                <div className="text-center py-12">
                  <Key className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Admin Keys</h3>
                  <p className="text-muted-foreground mb-4">
                    Create an admin key to programmatically access the Admin API for managing models, docs, and more.
                  </p>
                  <Button onClick={() => setIsCreateAdminKeyOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Your First Admin Key
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-sm text-muted-foreground mb-4">
                    {adminKeys.length} {adminKeys.length === 1 ? 'key' : 'keys'} total
                  </div>
                  {adminKeys.map((adminKey) => (
                    <div
                      key={adminKey.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="space-y-1">
                        <div className="font-medium">{adminKey.name}</div>
                        <div className="flex items-center space-x-2">
                          <code className="text-sm font-mono text-muted-foreground">
                            {adminKey.keyPrefix}••••••••••••••••
                          </code>
                        </div>
                        <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                          {adminKey.createdAt && (
                            <span className="flex items-center">
                              <Calendar className="h-3 w-3 mr-1" />
                              Created {new Date(adminKey.createdAt).toLocaleDateString()}
                            </span>
                          )}
                          {adminKey.expiresAt && (
                            <span>Expires {new Date(adminKey.expiresAt).toLocaleDateString()}</span>
                          )}
                          {adminKey.lastUsedAt && (
                            <span>Last used {new Date(adminKey.lastUsedAt).toLocaleDateString()}</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {adminKey.scopes.map((scope) => (
                            <span key={scope} className="text-xs bg-muted px-2 py-0.5 rounded">
                              {scope}
                            </span>
                          ))}
                        </div>
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Revoke Admin Key</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to revoke "{adminKey.name}"? This action cannot be undone and any applications using this key will stop working.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleRevokeAdminKey(adminKey.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Revoke
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Notifications Tab - Available to all members */}
        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Bell className="h-5 w-5" />
                <CardTitle>Notifications</CardTitle>
              </div>
              <CardDescription>
                Configure how you receive notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Email Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Receive important updates via email
                  </p>
                </div>
                <Switch
                  checked={emailNotifications}
                  onCheckedChange={setEmailNotifications}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Weekly Reports</Label>
                  <p className="text-sm text-muted-foreground">
                    Get a weekly summary of your analytics
                  </p>
                </div>
                <Switch
                  checked={weeklyReports}
                  onCheckedChange={setWeeklyReports}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Alert Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Get notified about important events
                  </p>
                </div>
                <Switch
                  checked={alertsEnabled}
                  onCheckedChange={setAlertsEnabled}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab - Admin Only */}
        {canModifySettings && (
        <TabsContent value="security" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Shield className="h-5 w-5" />
                <CardTitle>Security</CardTitle>
              </div>
              <CardDescription>
                Manage security settings for your organization
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <div className="font-medium">Require Two-Factor Authentication</div>
                  <div className="text-sm text-muted-foreground">
                    Require all organization members to enable two-factor authentication
                  </div>
                </div>
                <Switch
                  checked={require2FA}
                  onCheckedChange={setRequire2FA}
                />
              </div>
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <div className="font-medium">Active Sessions</div>
                  <div className="text-sm text-muted-foreground">
                    View and manage your active login sessions
                  </div>
                </div>
                <Button variant="outline">Manage</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>
    </div>
  )
}
